import {
  BarsOutlined,
  LineChartOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import {
  Card,
  Col,
  Empty,
  Flex,
  Form,
  Row,
  Segmented,
  Skeleton,
  Tooltip,
  Typography,
  theme,
  Button,
} from "antd";
import { useEffect, useState, useContext } from "react";
import CustomLineChart from "../../components/common/CustomLineChart";
import TrendFilter from "../../components/trending/TrendFilter";
import SummaryTable from "../../components/trending/SummaryTable";
import TrendTableView from "../../components/trending/TrendTableView";
import useFetch from "../../hooks/useFetch";
import dayjs from "dayjs";
import { IndexContext } from "../../context/IndexContext";
const { useToken } = theme;

function PowerTrendPage() {
  const { token } = useToken();
  const { Title } = Typography;
  const [trendView, setTrendView] = useState("Trend");
  const [filterForm] = Form.useForm();
  const [filterValues, setFilterValues] = useState(null);

  const [fetchWrapper] = useFetch();
  const { siteSelected, messageApi } = useContext(IndexContext);
  const [loading, setLoading] = useState(false);

  const filename = `PowerDemandTrend_${dayjs().format(
    "YYYY-MM-DDTHH:mm:ss"
  )}.xlsx`;

  // date type selection
  const dateTypeOptions = [
    { label: "Day", value: "date" },
    { label: "Month", value: "month" },
    { label: "Year", value: "year" },
  ];
  const [dateTypeSelection, setDateTypeSelection] = useState(
    dateTypeOptions[0].value
  );

  // power trend data
  const [powerTrendData, setPowerTrendData] = useState([]);
  const [powerTrendSummaryData, setPowerTrendSummaryData] = useState([]);
  const [powerTrendTableData, setPowerTrendTableData] = useState([]);

  const applyFilter = (value) => {
    setFilterValues(value);
  };

  const resetFilter = () => {
    setFilterValues(null);
  };

  /* API to get power trend data */
  function getPowerTrendData() {
    setLoading(true);
    fetchWrapper({
      endpoint_url: "trending/getPowerTrendData",
      body: {
        start_date_time: filterValues.start.format("YYYY-MM-DD HH:mm:ss"),
        end_date_time: filterValues.end.format("YYYY-MM-DD HH:mm:ss"),
        device_view: filterValues.device_view,
        devices: filterValues.device,
        date_type: dateTypeSelection,
      },
      onSuccess: (fetch_output) => {
        setLoading(false);
        if (fetch_output.status === true) {
          setPowerTrendData(fetch_output.power_demand_trend_data);
          setPowerTrendSummaryData(fetch_output.power_demand_summary_data);
          setPowerTrendTableData(fetch_output.power_demand_table_data);
        }
      },
    });
  }

  // function to download the file
  function downloadFile() {
    return new Promise((resolve, reject) => {
      fetchWrapper({
        endpoint_url: "trending/downloadFile",
        body: {
          table_data: powerTrendTableData,
          summary_data: powerTrendSummaryData,
          table_data_sheet_name: "Power Demand Trend",
          summary_data_sheet_name: "Power Demand Summary",
        },
        json_output: false,
        onSuccess: (response) => {
          // returned a json response.
          if (response.headers.get("content-type") === "application/json") {
            response.json().then((data) => {
              if (Object.keys(data).includes("error")) {
                // has issue with the download file
                messageApi.open({
                  type: "error",
                  content: "Failed to download file",
                });
                resolve(false);
              } else {
                // returned something else.
                messageApi.open({
                  type: "error",
                  content: "Failed to download file.",
                });
                resolve(false);
              }
            });
          } else {
            // no issue with the response. returned a spreadsheet
            response.blob().then((blob) => {
              const url = window.URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = url;
              a.download = filename;
              document.body.appendChild(a);
              a.click();
              a.remove();
              window.URL.revokeObjectURL(url);
              resolve(true);
            });
          }
        },
        onResponseError: (err) => {
          messageApi.open({
            type: "error",
            content: "Failed to download file",
          });
          resolve(false);
        },
      });
    });
  }

  useEffect(() => {
    if (filterValues && siteSelected) {
      getPowerTrendData();
    } else {
      setPowerTrendData([]);
      setPowerTrendSummaryData([]);
      setPowerTrendTableData([]);
    }
  }, [filterValues, dateTypeSelection]);

  useEffect(() => {
    setPowerTrendData([]);
    setPowerTrendSummaryData([]);
    setPowerTrendTableData([]);
    setFilterValues(null);
  }, [siteSelected]);

  return (
    <>
      <Row gutter={[12, 12]}>
        <Col span={24}>
          <TrendFilter
            form={filterForm}
            applyFilter={applyFilter}
            resetFilter={resetFilter}
          />
        </Col>
        <Col span={24}>
          <Card>
            <Flex
              justify="space-between"
              align="center"
              className="mb-2"
              wrap={true}
              gap={8}
            >
              <Title level={5} className="m-0">
                POWER DEMAND TRENDING
              </Title>
              <Flex gap={8} wrap={true}>
                <Segmented
                  options={dateTypeOptions}
                  onChange={(value) => {
                    setDateTypeSelection(value);
                  }}
                  value={dateTypeSelection}
                />

                <Segmented
                  options={[
                    { value: "Trend", icon: <LineChartOutlined /> },
                    { value: "Table", icon: <BarsOutlined /> },
                  ]}
                  value={trendView}
                  onChange={(value) => setTrendView(value)}
                />

                <Tooltip
                  title={
                    powerTrendTableData.length === 0 &&
                    powerTrendSummaryData.length === 0
                      ? "No data to download"
                      : ""
                  }
                >
                  <Button
                    icon={<DownloadOutlined />}
                    onClick={downloadFile}
                    disabled={
                      powerTrendData == null || powerTrendData?.length === 0
                    }
                  />
                </Tooltip>
              </Flex>
            </Flex>

            {loading ? (
              <div style={{ height: 520 }}>
                <Skeleton />
              </div>
            ) : (
              <div>
                {trendView === "Trend" ? (
                  <>
                    {powerTrendData.length > 0 ? (
                      <CustomLineChart
                        height={450}
                        data={powerTrendData}
                        themeToken={token}
                      ></CustomLineChart>
                    ) : (
                      <Flex
                        style={{ height: 450 }}
                        align="center"
                        justify="center"
                      >
                        <Empty />
                      </Flex>
                    )}
                    <SummaryTable data={powerTrendSummaryData}></SummaryTable>
                  </>
                ) : (
                  <>
                    <TrendTableView
                      data={powerTrendTableData}
                      summaryData={powerTrendSummaryData}
                    />
                  </>
                )}
              </div>
            )}
          </Card>
        </Col>
      </Row>
    </>
  );
}

export default PowerTrendPage;
